import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { WidgetError } from "@/components/WidgetError";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/auth";

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", username: "", password: "", role: "truckstop_staff",
    receives_billing_notifications: false, receives_system_updates: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/users", { params: { scope: "tenant", limit: 100 } });
      setItems(data.items);
    } catch (e) {
      setError(e);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/users", { ...form });
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", username: "", password: "", role: "truckstop_staff",
                receives_billing_notifications: false, receives_system_updates: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}?`)) return;
    try { await api.post(`/users/${u.id}/deactivate`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label-overline">Management</div>
          <h1 className="text-3xl font-black tracking-tight mt-1">Users</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="usr-new" className="rounded bg-blue-700 hover:bg-blue-700">+ New user</Button>
          </DialogTrigger>
          <DialogContent className="rounded">
            <DialogHeader><DialogTitle>New tenant user</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">Email</Label>
                  <Input data-testid="usr-email" type="email" required
                         className="rounded h-10 mt-1 font-mono"
                         value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label className="label-overline">Username (optional)</Label>
                  <Input data-testid="usr-username" className="rounded h-10 mt-1 font-mono"
                         value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                </div>
                <div>
                  <Label className="label-overline">Role</Label>
                  <select data-testid="usr-role"
                          className="w-full h-10 mt-1 border border-slate-300 rounded px-2 font-mono"
                          value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="truckstop_admin">truckstop_admin</option>
                    <option value="truckstop_staff">truckstop_staff</option>
                  </select>
                </div>
                <div>
                  <Label className="label-overline">Initial password (≥ 8)</Label>
                  <Input data-testid="usr-password" minLength={8} required
                         className="rounded h-10 mt-1 font-mono"
                         value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" className="rounded" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="usr-create" className="rounded bg-blue-700 hover:bg-blue-700"
                      onClick={create}
                      disabled={!form.email || !form.password || form.password.length < 8}>Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200">
        {error ? (
          <div className="p-4">
            <WidgetError
              error={error}
              onRetry={load}
              title="Could not load users"
              testId="users-error"
            />
          </div>
        ) : (
        <table className="tbl w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="label-overline text-slate-600">
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Active</th>
              <th className="text-left px-4 py-3">Last login</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No users</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`usr-row-${u.email}`}>
                <td className="px-4 py-3 font-mono">{u.email}</td>
                <td className="px-4 py-3">{ROLE_LABELS[u.role]}</td>
                <td className="px-4 py-3">
                  <span className={`pill ${u.is_active ? "pill-active" : "pill-cancelled"}`}>{u.is_active ? "active" : "off"}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{u.last_login_at ? fmtDate(u.last_login_at) : "—"}</td>
                <td className="px-4 py-3">
                  {u.is_active && (
                    <button onClick={() => deactivate(u)}
                            data-testid={`usr-off-${u.email}`}
                            className="text-red-600 hover:underline text-sm">Deactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
